import { MfsLoggerContext, MfsTelemetrySink } from '@metaos/mfs-telemetry';
import { MfsHostConfig } from '../store/mfsHost';
import { MfsLocalStore } from '@metaos/mfs-local-store';

/** Configuration options. */
export type MfsConfig = {
  /** The current app (or node) host that's interacting with the Mfs store. */
  hostConfig?: MfsHostConfig;

  /**
   * Optional value to override default domain used for the MfsService requests. It can be used to configure the Mfs
   * Loader to point at test versions of the MfsService used for integration or pre-production testing. If it is not
   * provided the world wide production domain for the MfsService is used ("api.mfs.microsoft.com").
   */
  mfsServiceDomain?: string;

  /**
   * Optional value that allows the application to specify which containerId to load. If this value is not provided the
   * users default MFS containerId will be returned. If the user does not have access to the specified containerId then
   * the MfsService will return an HTTP 401 error and the connection to the storage will fail.
   */
  containerId?: string;

  /** Session Id set by the client. Used for telemetry purposes */
  sessionId: string;

  /**
   * The milliseconds after which Fluid disconnected event is raised. Note that `MfsClient` Listens to the Fluid
   * connectivity events & raises `fluidDisconnected` if the container doesn't get re-`connected` within
   * `connectionTimeoutMs` milliseconds after it gets `disconnected`.
   */
  connectionTimeoutMs?: number;

  /**
   * Option to wait for the Pod to get the latest updates before loading. The default behavior will connect to the Pod
   * as is and fetch the updates after connecting. These updates may be visible to the user. Waiting for the updates
   * will incur a performance impact, but the updates won't be visible.
   */
  waitForUpdates?: boolean;

  /**
   * Optional telemetry sink provided by the host app. If a valid instance is not provided, MFS sets up its own instance
   * for the client.
   */
  sink?: MfsTelemetrySink;

  /**
   * Optional callback to resolve telemetry sink provided by the host app. If a valid instance is not provided, MFS sets
   * up its own instance for the client. If sinkCallback is passed, it will take preference over setLoggingContext and sink param
   */
  sinkCallback?: () => Promise<MfsTelemetrySink>;

  /** The deployment ring the client is running in. */
  clientRing?: string;

  /** Platform/app from where the app is launched. For e.g., Inprogress app launched from teams (appHostingPlatform) */
  appHostingPlatform?: string;

  /** Callback function to set the logger context */
  setLoggingContext?: SetLoggingContext;

  /** Local store instance that caches information in browser's storage */
  localStore?: MfsLocalStore;
};

/** The default connection timeout value in milliseconds. */
export const DEFAULT_CONNECTION_TIMEOUT_MS = 5000;

/** Function declaration for setLoggingContext field. */
export type SetLoggingContext = (loggerContext: MfsLoggerContext, sink?: MfsTelemetrySink) => void;
